import { withFaroRouterInstrumentation } from "@grafana/faro-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "antd";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, RouterProvider } from "react-router";

import { ComingSoon } from "design-system/components/ComingSoon";
import { ThemeProvider } from "design-system/components/ThemeProvider";
import { NotificationsProvider } from "design-system/contexts/NotificationsContext";
import { featureFlags } from "shell/platform/config/featureFlags";
import { initI18n } from "shell/platform/lib/i18n";

import { initAmplitude } from "./analytics/amplitude";
import { initFaro, isFaroEnabled } from "./analytics/faro";
import { sandboxRoutes } from "./routes";

import "design-system/reset.css";
import "@fontsource-variable/space-grotesk";
import "@fontsource-variable/jetbrains-mono/index.css";

initI18n();

// Initialise Grafana Frontend Observability (Faro) for sandbox production
initFaro();

// Initialise Amplitude product analytics for sandbox production
initAmplitude();

const isSandboxEnabled = featureFlags.enableSandbox;

let router = createBrowserRouter(sandboxRoutes);

if (isFaroEnabled) {
  router = withFaroRouterInstrumentation(router);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export const SandboxEntry = () => {
  const { t } = useTranslation(["sandbox"]);

  if (!isSandboxEnabled) {
    return (
      <ComingSoon
        title={t("components.comingSoon.title")}
        subTitle={t("components.comingSoon.subTitle")}
      />
    );
  }

  return <SandboxApp />;
};

export const SandboxApp = () => {
  return (
    <NotificationsProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </NotificationsProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <ThemeProvider>
    <App>
      <SandboxEntry />
    </App>
  </ThemeProvider>,
);
